import React from 'react';
import _isEmpty from 'lodash/isEmpty';
import Markdown from '@scale/frontend-lib/markdown';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { makeStyles } from 'design-system';
import { InstructionComponentType } from '@scale/shared/defaults';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  IInstructionExampleJson,
  RuleInstructionJson,
} from '@scale/shared/projectInstruction/types';
import Section from './section';
import Example from './example';
import { iconPropsByType } from '../constants';
import { SelectedPathType } from '../types';
import { useProjectInstructionContext } from '../projectInstructionContext';

const Rule = ({
  contentData,
  setSelectedPath,
}: {
  contentData: RuleInstructionJson;
  setSelectedPath: React.Dispatch<React.SetStateAction<SelectedPathType>>;
}) => {
  const classes = useStyles();
  const { title, examples, description, labels, attributes } = contentData;
  const { isMobileView } = useProjectInstructionContext();

  const ItemList = ({
    items,
    iconName,
    onClick,
  }: {
    items: $FIXME;
    iconName: IconProp;
    onClick: (title: string) => void;
  }) => {
    return (
      <div className={classes.itemWrapper}>
        {items.map(({ title, id }: { title: string; id: string }, index: number) => {
          return (
            <span className={classes.item} key={index} onClick={() => onClick(id)}>
              <span className={classes.iconBox}>
                <FontAwesomeIcon icon={iconName} />
              </span>
              <strong>{title}</strong>
              <FontAwesomeIcon icon="chevron-right" />
            </span>
          );
        })}
      </div>
    );
  };

  return (
    <div style={{ display: 'grid' }}>
      {!isMobileView && (
        <div className={classes.titleContainer}>
          <h3 className={classes.title}>{title}</h3>
          {contentData.hiddenFromCustomer && (
            <FontAwesomeIcon className={classes.hideIcon} icon="eye-slash" />
          )}
        </div>
      )}
      {description && (
        <Section sectionName="description">
          <div
            dangerouslySetInnerHTML={{
              __html: Markdown.render(description || ''),
            }}
          />
        </Section>
      )}
      {!_isEmpty(labels) && (
        <Section sectionName="applied labels">
          <ItemList
            items={labels}
            iconName={iconPropsByType[InstructionComponentType.Label].iconName}
            onClick={title => {
              setSelectedPath([InstructionComponentType.Label, title]);
            }}
          />
        </Section>
      )}
      {!_isEmpty(attributes) && (
        <Section sectionName="applied attributes">
          <ItemList
            items={attributes}
            iconName={iconPropsByType[InstructionComponentType.Attribute].iconName}
            onClick={(title: string) => {
              setSelectedPath([InstructionComponentType.Attribute, title]);
            }}
          />
        </Section>
      )}
      {!!examples?.length && (
        <Section sectionName="examples" isNew={false}>
          {examples.map((example: IInstructionExampleJson, index: number) => {
            return (
              <div
                key={`${index}-${index}`}
                style={{
                  borderBottom: index !== examples.length - 1 ? `3px solid #E6E6E6` : 'transparent',
                }}
              >
                <Example key={index} example={example} />
              </div>
            );
          })}
        </Section>
      )}
    </div>
  );
};

const useStyles = makeStyles(theme => ({
  title: {
    fontWeight: 600,
  },

  itemWrapper: {
    display: 'flex',
    flexWrap: 'wrap',
  },

  item: {
    border: `1px solid ${theme.colors.gray10}`,
    padding: 8,
    margin: '10px 10px 10px 0px',
    borderRadius: 6,
    display: 'flex',
    alignItems: 'center',
    '& > strong': {
      paddingRight: 20,
    },

    '&:hover': {
      cursor: 'pointer',
      background: theme.colors.gray01,
    },
  },

  iconBox: {
    height: 20,
    width: 20,
    background: theme.colors.gray10,
    borderRadius: 2,
    marginRight: 10,
    display: 'inline-flex',
    justifyContent: 'center',
    alignItems: 'center',
  },

  hideIcon: {
    color: theme.colors.gray50,
  },

  titleContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
}));

export default Rule;
